import React, { useEffect, useMemo } from 'react';
import L, { LatLngBoundsExpression } from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import styles from './GeoPointMap.module.scss';
import { clusterGeoPointMarkers, GeoPointDisplayMarker, GeoPointMarker } from './GeoPointMapModel';

const defaultTileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const defaultTileAttribution = '&copy; OpenStreetMap contributors';
const defaultCenter: [number, number] = [-36.8485, 174.7633];
const defaultZoom = 2;
const singleMarkerZoom = 10;

interface GeoPointMapProps {
    ariaLabel: string;
    emptyMessage: string;
    markers: readonly GeoPointMarker[];
    className?: string;
}

interface FitMapViewportProps {
    markers: readonly GeoPointDisplayMarker[];
}

const getTileUrl = () => process.env.REACT_APP_MAP_TILE_URL || defaultTileUrl;

const getTileAttribution = () => process.env.REACT_APP_MAP_TILE_ATTRIBUTION || defaultTileAttribution;

const buildIcon = (marker: GeoPointDisplayMarker) => {
    const countLabel = marker.pinCount > 1 ? marker.pinCount.toLocaleString() : '';
    const variantClass = styles[marker.variant] ?? styles.default;
    const clusterClass = marker.markers.length > 1 ? styles.cluster : '';
    const liveClass = marker.isLive ? styles.live : '';

    return L.divIcon({
        className: styles.pinIcon,
        html: `<span class="${styles.pin} ${variantClass} ${clusterClass} ${liveClass}">${countLabel}</span>`,
        iconAnchor: [13, 30],
        popupAnchor: [0, -28]
    });
};

const renderDetails = (details: GeoPointDisplayMarker['details']) => {
    if (!details) {
        return null;
    }

    if (Array.isArray(details)) {
        return (
            <ul className={styles.popupList}>
                {details.slice(0, 6).map((detail, index) => (
                    <li key={`${detail}-${index}`}>{detail}</li>
                ))}
            </ul>
        );
    }

    return <p className={styles.popupDetails}>{details}</p>;
};

const FitMapViewport = ({ markers }: FitMapViewportProps) => {
    const map = useMap();

    useEffect(() => {
        if (markers.length === 0) {
            return;
        }

        if (markers.length === 1) {
            map.setView([markers[0].latitude, markers[0].longitude], singleMarkerZoom);
            return;
        }

        const bounds: LatLngBoundsExpression = markers.map((marker): [number, number] => [
            marker.latitude,
            marker.longitude
        ]);
        map.fitBounds(bounds, { padding: [36, 36], maxZoom: singleMarkerZoom });
    }, [map, markers]);

    return null;
};

/** Reusable Leaflet map for validated WGS84 point markers with small-distance clustering. */
const GeoPointMap = ({ ariaLabel, emptyMessage, markers, className }: GeoPointMapProps) => {
    const displayMarkers = useMemo(() => clusterGeoPointMarkers(markers), [markers]);
    const mapClassName = className ? `${styles.mapShell} ${className}` : styles.mapShell;

    if (displayMarkers.length === 0) {
        return (
            <div className={styles.emptyState} role="status">
                {emptyMessage}
            </div>
        );
    }

    return (
        <div className={mapClassName} role="region" aria-label={ariaLabel}>
            <MapContainer
                center={defaultCenter}
                className={styles.map}
                scrollWheelZoom
                zoom={defaultZoom}
            >
                <TileLayer
                    attribution={getTileAttribution()}
                    url={getTileUrl()}
                />
                <FitMapViewport markers={displayMarkers} />
                {displayMarkers.map((marker) => (
                    <Marker
                        key={marker.id}
                        icon={buildIcon(marker)}
                        position={[marker.latitude, marker.longitude]}
                    >
                        <Popup>
                            <div className={styles.popup}>
                                <strong className={styles.popupTitle}>{marker.label}</strong>
                                {marker.description ? (
                                    <p className={styles.popupDescription}>{marker.description}</p>
                                ) : null}
                                {marker.pinCount > 1 ? (
                                    <p className={styles.popupCount}>{marker.pinCount.toLocaleString()} total</p>
                                ) : null}
                                {renderDetails(marker.details)}
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
};

export default GeoPointMap;
